import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 handling. History: the previous interceptor hard-redirected to
// /login on ANY 401 from any endpoint, which was kicking Laura +
// Marsha off the clock page every time the Sidebar's 60-second
// /notifications/counts poll came back 401 for whatever reason (stale
// token, race with a background password reset, etc.). The clock-path
// exclusion didn't help because it wasn't the clock endpoint's 401
// that redirected them — it was the sidebar's background poll.
//
// New rule: the ONLY 401 that ends the session is one from /auth/me.
// That's the one authoritative "is this token still valid" endpoint.
// AuthContext calls it explicitly on mount + periodically; if THAT
// 401s, the session is really dead. Any other endpoint's 401 is
// silently re-thrown — the caller shows a friendly error, but nothing
// wipes localStorage or navigates. That means a flaky background poll
// can never boot a staff member off the clock page again.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error.config || {};
    const url = cfg.url || '';
    const method = (cfg.method || 'get').toUpperCase();
    const status = error.response?.status;
    const onLoginPage = window.location.pathname === '/login';
    const wasLoggedIn = !!localStorage.getItem('portal_token');

    // ── Central failure logging ──────────────────────────────────────────
    // Pages overwhelmingly use `catch { toast.error('Failed') }` — no error
    // binding, no console.error (299 catch blocks, 9 of which logged
    // anything). So a real failure left NO trace anywhere and presented as
    // "I clicked the button and nothing happened" — which is exactly how a
    // completed 1:1 was lost. Logging HERE makes every failed request
    // traceable regardless of how the calling page swallows it, without
    // having to touch all 299 call sites.
    console.error(
      `[api] ${method} ${url} failed${status ? ` — HTTP ${status}` : ' — no response (network/timeout)'}:`,
      error.response?.data ?? error.message
    );

    if (status === 401) {
      const isAuthMe = url.includes('/auth/me');
      if (isAuthMe && !onLoginPage) {
        // Real "session expired" signal — the token failed the
        // canonical validation. Clear state and let AuthContext /
        // ProtectedRoute redirect on the next render.
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        window.dispatchEvent(new CustomEvent('portal:session-expired'));
        toast.error('Your session expired — please log in again.', {
          id: 'session-expired', duration: 6000,
        });
      } else if (!onLoginPage && wasLoggedIn) {
        // A non-/auth/me 401 must NEVER end the session or navigate — that is
        // what used to kick teachers off the clock page mid-shift. But the
        // user still has to know WHY their action did nothing: a page's
        // generic "Failed" toast doesn't say "you are logged out and your
        // work was not saved". Say it plainly, deduped, and long enough to
        // actually read. No redirect, so nothing on screen is destroyed.
        toast.error(
          'Your session expired, so that did NOT save. Log in again in another tab, then retry — your work on this screen is still here.',
          { id: 'session-expired', duration: 10000 }
        );
      }
    }
    return Promise.reject(error);
  }
);

export default api;
